/**
 * @fileoverview Hook for product cancellation/returns data.
 * @module shared/hooks/dashboard/useProductCancellations
 */

import { useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  statisticsService,
  type TimeAggregation,
  type UnifiedStatisticsFilterDTO,
  type UnifiedStatisticsResponseDTO,
} from '@/lib/statisticsService'
import { FilterContext } from '@/shared/context/FilterContext'
import { prefixWithOrg, queryKeys } from '@/shared/utils/queryKeys'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'

/**
 * Cancellation data for a single product.
 */
export interface ProductCancellation {
  /** Sum of cancelled quantity for the selected period */
  quantity: number
  /** Sum of potential revenue loss for the selected period */
  potentialRevenueLoss: number
}

/**
 * Hook result for product cancellations data.
 */
export interface UseProductCancellationsResult {
  /** Map of SKU/product key to cancellation data */
  cancellationsMap: Map<string, ProductCancellation>
  /** Total cancellation quantity across all products */
  totalQuantity: number
  /** Total potential revenue loss across all products */
  totalRevenueLoss: number
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
}

/**
 * Map FilterContext aggregation to API `TimeAggregation`.
 */
function mapAggregation(a: string | null | undefined): TimeAggregation {
  const map: Record<string, TimeAggregation> = {
    Daily: 'DAILY',
    Weekly: 'WEEKLY',
    Monthly: 'MONTHLY',
    Quarterly: 'QUARTERLY',
    Yearly: 'YEARLY',
  }
  return a && map[a] ? map[a] : 'MONTHLY'
}

/**
 * Hook to fetch product cancellation/returns data from the statistics API.
 * Returns a map of SKU to cancellation metrics for efficient lookups.
 *
 * @returns Product cancellations map with loading and error states
 * @example
 * ```typescript
 * const { cancellationsMap, isLoading } = useProductCancellations();
 * const cancellation = cancellationsMap.get(sku);
 * if (cancellation) {
 *   console.log(`${cancellation.quantity} returns, ${cancellation.potentialRevenueLoss} loss`);
 * }
 * ```
 */
export function useProductCancellations(): UseProductCancellationsResult {
  const filterCtx = useContext(FilterContext)
  const { activeOrgId } = useActiveOrg()

  const { startDate, endDate, aggregation, countries, skus, tags } = filterCtx ?? {}

  // Compose API filter from context
  const { filter, timeAggregation } = useMemo<{
    filter: UnifiedStatisticsFilterDTO
    timeAggregation: TimeAggregation
  }>(() => {
    const f: UnifiedStatisticsFilterDTO = {}
    if (startDate) f.startDate = toDateOnlyStringLocal(startDate)
    if (endDate) f.endDate = toDateOnlyStringLocal(endDate)
    if (countries?.length) f.countries = countries
    if (skus?.length) f.skus = skus
    if (tags?.length) f.tags = tags
    return { filter: f, timeAggregation: mapAggregation(aggregation) }
  }, [startDate, endDate, aggregation, countries, skus, tags])

  const query = useQuery<UnifiedStatisticsResponseDTO>({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.cancellationStatistics(filter, ['TIME', 'PRODUCT'], timeAggregation)
        )
      : queryKeys.cancellationStatistics(filter, ['TIME', 'PRODUCT'], timeAggregation),
    queryFn: () =>
      statisticsService.getCancellationStatistics(filter, ['TIME', 'PRODUCT'], timeAggregation),
    enabled: Boolean(activeOrgId && startDate && endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  // Aggregate across TIME for each PRODUCT
  const { cancellationsMap, totalQuantity, totalRevenueLoss } = useMemo(() => {
    const map = new Map<string, ProductCancellation>()
    let totalQty = 0
    let totalLoss = 0

    const rows = query.data?.data ?? []
    for (const row of rows) {
      const product = String(row.dimensions['product'] ?? '')
      if (!product) continue

      const qty = Number(row.metrics.cancellationCount ?? 0)
      const loss = Number(row.metrics.potentialRevenueLoss ?? 0)

      const existing = map.get(product)
      if (existing) {
        existing.quantity += qty
        existing.potentialRevenueLoss += loss
      } else {
        map.set(product, { quantity: qty, potentialRevenueLoss: loss })
      }

      totalQty += qty
      totalLoss += loss
    }

    return { cancellationsMap: map, totalQuantity: totalQty, totalRevenueLoss: totalLoss }
  }, [query.data])

  return {
    cancellationsMap,
    totalQuantity,
    totalRevenueLoss,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
